import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { FileSignature } from "lucide-react";
import DashboardCard from "../components/DashboardCard";
import DashboardCardSkeleton from "../components/DashboardCardSkeleton";
import DashboardEmptyState from "../components/DashboardEmptyState";
import DashboardPagination from "../components/DashboardPagination";
import { DASHBOARD_SIGNATURES_PAGE_SIZE } from "../constants/dashboardListPagination";
import {
  fetchClickSignEnvelopeDownloadUrl,
  fetchRentContractDownloadUrl,
  notifyClickSignEnvelope,
} from "../services/ownerPatrimonyService";
import {
  IPaginationMeta,
  IPendingSignatureItem,
  IPendingSignaturesData,
  OwnerDocumentStatusFilter,
  OwnerPatrimonyRequestStatus,
} from "../types/ownerDashboard.types";
import { filterPendingSignaturesByTab } from "../utils/filterPendingSignaturesByTab";
import PendingSignatureCard from "./PendingSignatureCard";
import { RentedPropertyCatalog } from "../utils/buildRentedPropertyCatalog";
import PendingSignaturesListSkeleton from "./PendingSignaturesListSkeleton";
import SignContractModal from "./SignContractModal";

interface PendingSignaturesProps {
  data: IPendingSignaturesData | null;
  status: OwnerPatrimonyRequestStatus;
  statusFilter: OwnerDocumentStatusFilter;
  pagination?: IPaginationMeta | null;
  isForbidden?: boolean;
  successMessage: string | null;
  smDown: boolean;
  lgDown?: boolean;
  onStatusFilterChange: (status: OwnerDocumentStatusFilter) => void;
  onPageChange: (page: number) => void;
  onRetry: () => Promise<void>;
  onRefreshList: () => Promise<void>;
  onSigned: () => Promise<void>;
  onDismissSuccessMessage: () => void;
  rentedPropertyCatalog?: RentedPropertyCatalog;
}

function resolveAxiosMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const message = error.response?.data?.message;
    if (typeof message === "string" && message.trim()) {
      return message;
    }
  }

  return fallback;
}

export default function PendingSignatures({
  data,
  status,
  statusFilter,
  pagination = null,
  isForbidden = false,
  successMessage,
  smDown,
  lgDown = false,
  onStatusFilterChange,
  onPageChange,
  onRetry,
  onRefreshList,
  onSigned,
  onDismissSuccessMessage,
  rentedPropertyCatalog,
}: PendingSignaturesProps) {
  const padding = smDown ? "0.875rem" : lgDown ? "1rem" : "1.125rem";
  const [selectedItem, setSelectedItem] = useState<IPendingSignatureItem | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [notifyingId, setNotifyingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [localSuccessMessage, setLocalSuccessMessage] = useState<string | null>(null);

  const pendingCount = data?.pending_count ?? 0;
  const signedCount = data?.signed_count ?? 0;
  const displayedSuccessMessage = localSuccessMessage || successMessage;

  const visibleItems = useMemo(
    () => filterPendingSignaturesByTab(data?.items ?? [], statusFilter),
    [data?.items, statusFilter]
  );
  const isListLoading = status === "loading";
  const listAreaRef = useRef<HTMLDivElement>(null);
  const [reservedListHeight, setReservedListHeight] = useState<number | null>(null);
  const [reservedListCount, setReservedListCount] = useState(DASHBOARD_SIGNATURES_PAGE_SIZE);

  useLayoutEffect(() => {
    if (isListLoading) {
      return;
    }

    const height = listAreaRef.current?.offsetHeight;
    if (height) {
      setReservedListHeight(height);
    }

    if (visibleItems.length > 0) {
      setReservedListCount(visibleItems.length);
    }
  }, [isListLoading, visibleItems]);

  const handleCloseModal = useCallback(() => setSelectedItem(null), []);

  const handleSign = useCallback(
    async (item: IPendingSignatureItem) => {
      setActionError(null);
      setLocalSuccessMessage(null);

      if (item.sign_mode === "email") {
        if (!item.envelope_id || !data?.signing.clicksign_notify_endpoint) {
          setActionError("Não foi possível enviar o link de assinatura para este documento.");
          return;
        }

        setNotifyingId(item.id);

        try {
          await notifyClickSignEnvelope(
            item.envelope_id,
            data.signing.clicksign_notify_endpoint
          );
          setLocalSuccessMessage(
            "Link de assinatura enviado por e-mail. O documento continua pendente até as assinaturas serem concluídas."
          );
          // Soft refresh only — notifying is not the same as signing.
          await onRefreshList();
        } catch (error) {
          setActionError(
            resolveAxiosMessage(
              error,
              "Não foi possível enviar o link de assinatura. Tente novamente."
            )
          );
        } finally {
          setNotifyingId(null);
        }

        return;
      }

      setSelectedItem(item);
    },
    [data?.signing.clicksign_notify_endpoint, onRefreshList]
  );

  const handleDownload = useCallback(
    async (item: IPendingSignatureItem) => {
      setActionError(null);
      setDownloadingId(item.id);

      try {
        if (item.download_url) {
          window.open(item.download_url, "_blank", "noopener,noreferrer");
          return;
        }

        if (item.signed_file_url || item.ziped_file_url) {
          window.open(
            item.signed_file_url || item.ziped_file_url || "",
            "_blank",
            "noopener,noreferrer"
          );
          return;
        }

        if (
          item.source === "clicksign_envelope" &&
          item.envelope_id &&
          data?.signing.clicksign_download_endpoint
        ) {
          const downloadUrl = await fetchClickSignEnvelopeDownloadUrl(
            item.envelope_id,
            data.signing.clicksign_download_endpoint
          );
          window.open(downloadUrl, "_blank", "noopener,noreferrer");
          return;
        }

        if (item.rent_process_id == null || !data?.signing.download_endpoint) {
          throw new Error("Download endpoint not available");
        }

        const downloadUrl = await fetchRentContractDownloadUrl(
          item.rent_process_id,
          data.signing.download_endpoint
        );
        window.open(downloadUrl, "_blank", "noopener,noreferrer");
      } catch (error) {
        setActionError(
          resolveAxiosMessage(
            error,
            "Não foi possível baixar o contrato. Tente novamente mais tarde."
          )
        );
      } finally {
        setDownloadingId(null);
      }
    },
    [data?.signing.clicksign_download_endpoint, data?.signing.download_endpoint]
  );

  if (status === "idle" || (status === "loading" && !data)) {
    return <DashboardCardSkeleton minHeight="min-h-[14rem]" lines={3} />;
  }

  if (status === "error") {
    return (
      <DashboardCard padding={padding} className="flex h-full min-h-0 flex-col">
        <DashboardEmptyState
          align="center"
          icon={FileSignature}
          title="Contratos de locação"
          description={
            isForbidden
              ? "Você não tem permissão para acessar os contratos. Verifique com o suporte se a funcionalidade está habilitada no ambiente."
              : "Não foi possível carregar os contratos de locação."
          }
          action={
            !isForbidden ? (
              <button
                type="button"
                onClick={() => void onRetry()}
                className="rounded-full bg-[#6ea8ff] px-4 py-2 text-xs font-bold text-[#0d1117] transition-opacity hover:opacity-90"
              >
                Tentar novamente
              </button>
            ) : undefined
          }
          className="min-h-[12rem] justify-center"
        />
      </DashboardCard>
    );
  }

  const total = data?.total ?? data?.items.length ?? 0;
  const isPendingTab = statusFilter === "pending";
  const currentPage = pagination?.current_page ?? 1;
  const totalPages = pagination?.last_page ?? 1;

  return (
    <>
      <DashboardCard padding={padding} className="flex h-full min-h-0 flex-col">
        <div className="flex min-h-0 w-full flex-1 flex-col gap-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <FileSignature className="h-5 w-5 shrink-0 text-[#6ea8ff]" />
                <h2 className="text-base font-bold text-[#f0f6fc] sm:text-lg">
                  Contratos de locação
                </h2>
                {pendingCount > 0 && (
                  <span className="inline-flex min-w-6 items-center justify-center rounded-full bg-[#6ea8ff] px-2 py-0.5 text-[0.65rem] font-bold text-[#0d1117]">
                    {pendingCount}
                  </span>
                )}
              </div>
              <p className="mt-1 text-xs text-[#8b949e] sm:text-sm">
                Assine contratos pendentes ou baixe documentos já finalizados.
              </p>
            </div>
          </div>

          {data?.clicksign_error && (
            <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-3">
              <p className="text-xs text-amber-300 sm:text-sm">{data.clicksign_error}</p>
            </div>
          )}

          {displayedSuccessMessage && (
            <div className="flex items-start justify-between gap-3 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-3">
              <p className="text-xs text-emerald-300 sm:text-sm">{displayedSuccessMessage}</p>
              <button
                type="button"
                onClick={() => {
                  setLocalSuccessMessage(null);
                  onDismissSuccessMessage();
                }}
                className="shrink-0 text-xs font-semibold text-emerald-300 hover:underline"
              >
                Ok
              </button>
            </div>
          )}

          {actionError && (
            <div className="rounded-2xl border border-red-500/30 bg-red-500/10 p-3">
              <p className="text-xs text-red-300 sm:text-sm">{actionError}</p>
            </div>
          )}

          {total > 0 && (
            <div className="flex w-full gap-2 rounded-2xl border border-white/[0.08] bg-[#0d1117]/60 p-1">
              <button
                type="button"
                onClick={() => onStatusFilterChange("pending")}
                className={`flex-1 rounded-xl px-3 py-2 text-xs font-semibold transition-colors sm:text-sm ${
                  isPendingTab
                    ? "bg-[#6ea8ff] text-[#0d1117]"
                    : "text-[#8b949e] hover:text-[#f0f6fc]"
                }`}
              >
                Pendentes ({pendingCount})
              </button>
              <button
                type="button"
                onClick={() => onStatusFilterChange("signed")}
                className={`flex-1 rounded-xl px-3 py-2 text-xs font-semibold transition-colors sm:text-sm ${
                  !isPendingTab
                    ? "bg-[#6ea8ff] text-[#0d1117]"
                    : "text-[#8b949e] hover:text-[#f0f6fc]"
                }`}
              >
                Assinados ({signedCount})
              </button>
            </div>
          )}

          <div
            ref={listAreaRef}
            style={
              isListLoading && reservedListHeight
                ? { height: reservedListHeight, overflow: "hidden" }
                : undefined
            }
          >
            {isListLoading ? (
              <PendingSignaturesListSkeleton count={reservedListCount} />
            ) : visibleItems.length > 0 ? (
              <div className="flex flex-col gap-3">
                {visibleItems.map((item) => (
                  <PendingSignatureCard
                    key={item.id}
                    item={item}
                    isDownloading={downloadingId === item.id}
                    isNotifying={notifyingId === item.id}
                    rentedPropertyCatalog={rentedPropertyCatalog}
                    onSign={handleSign}
                    onDownload={handleDownload}
                  />
                ))}
              </div>
            ) : (
              <DashboardEmptyState
                align="center"
                icon={FileSignature}
                title={isPendingTab ? "Nenhum contrato pendente" : "Nenhum contrato assinado"}
                description={
                  isPendingTab
                    ? "Quando houver novos contratos para assinar, eles aparecerão aqui."
                    : "Contratos finalizados com todas as assinaturas aparecerão nesta aba."
                }
              />
            )}
          </div>

          <DashboardPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
          />
        </div>
      </DashboardCard>

      {selectedItem && data?.signing && (
        <SignContractModal
          item={selectedItem}
          signerKeyEndpoint={data.signing.signer_key_endpoint}
          onClose={handleCloseModal}
          onSigned={onSigned}
        />
      )}
    </>
  );
}
